import {
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';

import StudentGroups
  from './course/StudentGroups';

const SUPABASE_URL =
  process.env.REACT_APP_SUPABASE_URL;

const SUPABASE_KEY =
  process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY;


function headers() {
  return {
    apikey:
      SUPABASE_KEY,

    Authorization:
      'Bearer ' +
      SUPABASE_KEY
  };
}


function hasCourseGroup(
  enrolment
) {
  return Boolean(
    enrolment &&

    enrolment.stream !==
      null &&
    enrolment.stream !==
      undefined &&
    enrolment.stream !==
      '' &&

    enrolment.group_number !==
      null &&
    enrolment.group_number !==
      undefined &&
    enrolment.group_number !==
      ''
  );
}


export async function
studentHasAnyCurrentGroups(
  enrolments
) {
  const rows =
    (
      Array.isArray(
        enrolments
      )
        ? enrolments
        : []
    )
      .filter(
        row =>
          row?.id &&
          row?.courses?.id
      );


  /*
   * Ordinary course-level groups are stored
   * directly on the enrolment.
   */
  if (
    rows.some(
      hasCourseGroup
    )
  ) {
    return true;
  }


  /*
   * Assignment groups are stored in
   * assignment_group_memberships.
   */
  const ids =
    rows
      .map(
        row =>
          row.id
      )
      .filter(Boolean);


  if (!ids.length) {
    return false;
  }


  const response =
    await fetch(
      SUPABASE_URL +
        '/rest/v1/assignment_group_memberships' +
        '?enrolment_id=in.(' +
        ids
          .map(
            encodeURIComponent
          )
          .join(',') +
        ')' +
        '&select=id' +
        '&limit=1',

      {
        headers:
          headers()
      }
    );


  const text =
    await response.text();


  if (!response.ok) {
    throw new Error(
      'Checking student group membership failed (' +
      response.status +
      '): ' +
      (
        text ||
        response.statusText
      )
    );
  }


  const data =
    text
      ? JSON.parse(text)
      : [];


  return (
    Array.isArray(
      data
    ) &&
    data.length > 0
  );
}


function courseName(
  enrolment
) {
  const course =
    enrolment?.courses ||
    {};


  const title =
    Array.isArray(
      course.course_titles
    )
      ? course
          .course_titles[0]

      : course
          .course_titles;


  return (
    title?.name ||
    course.name ||
    'Course'
  );
}


function CourseGroups({
  session,
  enrolment
}) {
  const [
    count,
    setCount
  ] =
    useState(null);


  const onCount =
    useCallback(
      value =>
        setCount(
          Number(value) ||
          0
        ),
      []
    );


  /*
   * A current course with no group membership
   * should not take up any space on the global
   * page.
   */
  if (count === 0) {
    return null;
  }


  return (
    <section
      style={
        count === null
          ? styles.hidden
          : styles.courseSection
      }
    >
      <div
        style={
          styles.courseHeading
        }
      >
        <h2
          style={
            styles.courseTitle
          }
        >
          {courseName(
            enrolment
          )}
        </h2>


        {enrolment
          ?.courses
          ?.semesters
          ?.name && (
          <span
            style={
              styles.semester
            }
          >
            {
              enrolment
                .courses
                .semesters
                .name
            }
          </span>
        )}
      </div>


      <StudentGroups
        session={session}
        course={
          enrolment.courses
        }
        enrolment={
          enrolment
        }
        hideHeading
        hideIntro
        hideEmpty
        onGroupCountChange={
          onCount
        }
      />
    </section>
  );
}


export default function
StudentGlobalGroups({
  session,
  enrolments,
  compact = false
}) {
  const rows =
    useMemo(
      () =>
        (
          Array.isArray(
            enrolments
          )
            ? [
                ...enrolments
              ]
            : []
        )
          .filter(
            row =>
              row?.id &&
              row?.courses?.id
          )
          .sort(
            (a, b) =>
              courseName(a)
                .localeCompare(
                  courseName(b),
                  undefined,
                  {
                    numeric:
                      true
                  }
                )
          ),

      [enrolments]
    );


  const [
    hasAny,
    setHasAny
  ] =
    useState(null);


  useEffect(() => {
    let cancelled =
      false;


    studentHasAnyCurrentGroups(
      rows
    )
      .then(
        value => {
          if (
            !cancelled
          ) {
            setHasAny(
              value
            );
          }
        }
      )
      .catch(
        error => {
          console.error(
            'Checking global Groups availability failed:',
            error
          );


          if (
            !cancelled
          ) {
            setHasAny(
              false
            );
          }
        }
      );


    return () => {
      cancelled =
        true;
    };
  }, [
    rows
  ]);


  if (
    hasAny ===
    null
  ) {
    return (
      <div
        style={
          styles.muted
        }
      >
        Loading Groups...
      </div>
    );
  }


  if (!hasAny) {
    return (
      <div
        style={
          styles.empty
        }
      >
        You are not currently assigned to any groups.
      </div>
    );
  }


  return (
    <div
      style={
        styles.wrap
      }
    >
      {!compact && (
        <div
          style={
            styles.pageHeading
          }
        >
          <h1
            style={
              styles.pageTitle
            }
          >
            Groups
          </h1>

          <p
            style={
              styles.pageIntro
            }
          >
            Your current course and assignment groups are collected here.
          </p>
        </div>
      )}


      <div
        style={
          styles.courseList
        }
      >
        {rows.map(
          enrolment => (
            <CourseGroups
              key={
                enrolment.id
              }
              session={
                session
              }
              enrolment={
                enrolment
              }
            />
          )
        )}
      </div>
    </div>
  );
}


const styles = {
  wrap: {
    width:
      '100%'
  },

  hidden: {
    display:
      'none'
  },

  pageHeading: {
    marginBottom:
      22
  },

  pageTitle: {
    margin:
      '0 0 5px',

    color:
      '#1f2933',

    fontSize:
      24
  },

  pageIntro: {
    margin:
      0,

    color:
      '#66788a',

    fontSize:
      13,

    lineHeight:
      1.5
  },

  courseList: {
    display:
      'grid',

    gap:
      24
  },

  courseSection: {
    width:
      '100%'
  },

  courseHeading: {
    display:
      'flex',

    alignItems:
      'baseline',

    justifyContent:
      'space-between',

    gap:
      12,

    marginBottom:
      10,

    paddingBottom:
      8,

    borderBottom:
      '1px solid #d7dee5'
  },

  courseTitle: {
    margin:
      0,

    color:
      '#1f2933',

    fontSize:
      17
  },

  semester: {
    color:
      '#829ab1',

    fontSize:
      11
  },

  muted: {
    color:
      '#66788a',

    fontSize:
      14
  },

  empty: {
    padding:
      16,

    border:
      '1px solid #d7dee5',

    borderRadius:
      10,

    background:
      '#f7f9fa',

    color:
      '#66788a',

    fontSize:
      13
  }
};
